import { ACCOUNT_TYPE } from "@/constants";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";

/**
 * Auth0 RWA (cookie) flow — frontend helpers.
 *
 * The backend/edge serves the auth endpoints at `/api/auth/*` on the TitanHQ
 * host (the UI's own origin — NOT the app's Vite base and NOT the cross-origin
 * VITE_API_CLIENT host). The browser never sees a token; the httpOnly
 * `titanhq_session` cookie is managed entirely by the edge.
 */
export const AUTH0_BASE_PATH = "/api/auth";

/**
 * Profile for the signed-in Auth0 identity: user AND nested organisation.
 * Served on the UI's own origin (cookie-authenticated), NOT the cross-origin
 * VITE_API_CLIENT host, so it must be fetched with credentials via a bare axios
 * call — never the JWT `$http` instance.
 *
 * This stands in for the JWT claims the legacy flow decodes: the httpOnly
 * session cookie is opaque, so without this the app has no user name, email,
 * uuid or roles at all. Note the sibling `/profile/organisation/me` returns the
 * organisation ONLY — it cannot populate user identity.
 */
export const AUTH0_USER_PROFILE_PATH = "/api/v1/idp/profile/user/me";

export type Auth0SessionUser = {
  organisation?: { uuid?: string; type_name?: string } | null;
  organisation_uuid?: string;
  organisationUuid?: string;
  type_name?: string;
  account_type?: string;
  [key: string]: unknown;
};

export type Auth0Session = {
  authenticated: boolean;
  user?: Auth0SessionUser | null;
  roles?: string[];
  permissions?: string[];
};

export type Auth0OrganisationProfile = {
  uuid?: string | null;
  name?: string | null;
  type_uuid?: string | null;
  type_name?: string | null;
  services?: { uuid?: string; name?: string; seats_purchased?: number }[];
};

/**
 * `GET /api/v1/idp/profile/user/me`. Mirrors the `user` + `organisation` claims
 * the legacy flow reads off the JWT, which is why it maps straight onto
 * `buildAuthUser` in the authentication store.
 */
export type Auth0UserProfile = {
  uuid?: string | null;
  name?: string | null;
  email?: string | null;
  enabled_2fa?: boolean;
  roles?: string[];
  organisation?: Auth0OrganisationProfile | null;
};

export type BuildLoginUrlParams = {
  returnPath?: string | null;
  invitation?: string | null;
  organization?: string | null;
};

/**
 * Marker on the post-logout return URL. Auth0 sends the user back to the app's
 * own `/logout` route, so the Logout view needs this to tell the return pass
 * from the pass that initiates logout — without it the two bounce forever.
 */
export const AUTH0_POST_LOGOUT_PARAM = "post_logout";

export type BuildLogoutReturnUrlParams = {
  origin: string;
  basePath?: string;
  redirect?: string | null;
};

/**
 * Build the full-page redirect URL to the Auth0 login endpoint.
 * Root-relative so it is independent of the app's Vite base path.
 */
export const buildAuth0LoginUrl = ({
  returnPath,
  invitation,
  organization,
}: BuildLoginUrlParams = {}): string => {
  const params = new URLSearchParams();
  params.set("return", returnPath && returnPath !== "" ? returnPath : "/");

  if (invitation) params.set("invitation", invitation);
  if (organization) params.set("organization", organization);

  return `${AUTH0_BASE_PATH}/login?${params.toString()}`;
};

/**
 * Absolute URL Auth0 returns the user to after RP-initiated logout, passed to
 * the backend as `return` and forwarded by it as Auth0's `returnTo`.
 *
 * Auth0 rejects (400) any `returnTo` that is not registered in the tenant's
 * Allowed Logout URLs, and it matches on origin + path exactly — a trailing
 * slash fails, while a query string is ignored for the comparison. The
 * registered path is the app's own `/logout` route, so that is what we build,
 * carrying the post-logout marker and the original post-sign-in redirect.
 */
export const buildAuth0LogoutReturnUrl = ({
  origin,
  basePath = "/",
  redirect,
}: BuildLogoutReturnUrlParams): string => {
  // "/" and "/pui/" alike must yield exactly one slash before "logout".
  const base = basePath.replace(/\/+$/, "");
  const params = new URLSearchParams();
  params.set(AUTH0_POST_LOGOUT_PARAM, "1");
  if (redirect) params.set("redirect", redirect);

  return `${origin}${base}/logout?${params.toString()}`;
};

/**
 * Build the full-page redirect URL to the Auth0 logout endpoint.
 * Root-relative so it is independent of the app's Vite base path.
 *
 * With no return URL the backend omits `returnTo` and Auth0 falls back to the
 * tenant's default Allowed Logout URL.
 */
export const buildAuth0LogoutUrl = (returnUrl?: string | null): string => {
  if (!returnUrl) return `${AUTH0_BASE_PATH}/logout`;

  const params = new URLSearchParams();
  params.set("return", returnUrl);

  return `${AUTH0_BASE_PATH}/logout?${params.toString()}`;
};

/**
 * Resolve the scoped landing path for an authenticated Auth0 session from the
 * `organisation` block of the user profile, mirroring
 * `routeBasedOnRole` in the sign-in view. Returns null when the org uuid is
 * absent so callers can fall back safely (e.g. leave the user in place).
 */
export const resolveAuth0Landing = (
  organisation?: Auth0OrganisationProfile | null,
): string | null => {
  const orgUuid = organisation?.uuid;
  if (!orgUuid) return null;

  const type = organisation?.type_name;

  if (type === ACCOUNT_TYPE.MSP) return buildOrgUrl("msp", orgUuid);
  if (type === ACCOUNT_TYPE.CUSTOMER) return buildOrgUrl("customer", orgUuid);

  // System / Sales / Domain and anything else land on the system scope,
  // matching the sign-in view's default.
  return buildOrgUrl("system", orgUuid);
};

export default {
  AUTH0_BASE_PATH,
  AUTH0_USER_PROFILE_PATH,
  AUTH0_POST_LOGOUT_PARAM,
  buildAuth0LoginUrl,
  buildAuth0LogoutReturnUrl,
  buildAuth0LogoutUrl,
  resolveAuth0Landing,
};
